import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery, useMutation } from '../../hooks';
import { ordersAPI } from '../../services/api';
import {
  Modal, StatusBadge, Badge, LoadingPage, Button, Select,
} from '../common/UI';
import { format } from 'date-fns';

const STATUS_OPTS = ['pending', 'confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered', 'cancelled', 'return_requested', 'return_approved', 'pickup_scheduled', 'return_picked', 'refunded'];

const OrderDetailModal = ({ orderId, open, onClose, onUpdated, readOnly = false, hideUpdateSection = false }) => {
  const isReadOnly = readOnly || hideUpdateSection;
  const { data: order, loading, refetch } = useQuery(
    () => ordersAPI.detail(orderId),
    [orderId],
    { enabled: !!orderId && open }
  );
  const [newStatus, setNewStatus] = useState('');
  const [note, setNote] = useState('');
  const [updatingItemId, setUpdatingItemId] = useState(null);

  const { mutate: updateStatus, loading: updating } = useMutation(
    () => ordersAPI.updateStatus(orderId, { status: newStatus, note }),
    { successMsg: 'Status updated', onSuccess: () => { refetch(); onUpdated?.(); setNewStatus(''); } }
  );

  const handleItemStatusChange = async (itemId, targetStatus) => {
    if (!targetStatus) return;
    setUpdatingItemId(itemId);
    try {
      await ordersAPI.updateItemStatus(orderId, itemId, {
        status: targetStatus,
        note: `Item status updated to ${targetStatus}`
      });
      toast.success(`Item status updated to ${targetStatus.replace(/_/g, ' ')}`);
      refetch();
      onUpdated?.();
    } catch (err) {
      const msg = err.response?.data?.msg || err.response?.data?.message || 'Failed to update item status';
      toast.error(msg);
    } finally {
      setUpdatingItemId(null);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Order ${order?.order_number || '…'}`} size="xl">
      {loading ? <LoadingPage /> : order && (
        <div className="space-y-5 overflow-y-auto max-h-[80vh] px-1">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ['Status',   <StatusBadge status={order.status} />],
              ['Total',    <span className="font-mono font-bold">₹{Number(order.total_amount).toLocaleString()}</span>],
              ['Payment',  <StatusBadge status={order.payment?.status} />],
              ['Method',   <Badge variant="gray">{order.payment?.method?.toUpperCase()}</Badge>],
            ].map(([label, val]) => (
              <div key={label} className="rounded-xl bg-surface-50 p-3">
                <p className="text-xs text-surface-400 mb-1">{label}</p>
                {val}
              </div>
            ))}
          </div>

          {/* Shipping */}
          <div className="rounded-xl border border-surface-100 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-surface-500 mb-2">Shipping Address</p>
            <p className="text-sm font-medium">{order.shipping_address?.full_name}</p>
            <p className="text-sm text-surface-500">{order.shipping_address?.address_line1}, {order.shipping_address?.city}, {order.shipping_address?.state} — {order.shipping_address?.pincode}</p>
            <p className="text-sm text-surface-500">📞 {order.shipping_address?.phone}</p>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-surface-500">Items</p>
              <span className="text-xs text-surface-400">{order.items?.length || 0} items</span>
            </div>
            <div className="space-y-2">
              {order.items?.map(item => (
                <div key={item.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-surface-50 p-3 border border-surface-100/60">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-surface-900">{item.product_name}</p>
                      <div className="flex items-center gap-2 text-xs text-surface-400 mt-0.5">
                        <span>{item.product_sku || 'SKU'} × {item.quantity}</span>
                        {item.variant_name && <Badge variant="gray" size="xs">{item.variant_name}</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-surface-100">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={item.status || order.status} />
                      {!isReadOnly && (
                        <select
                          className="text-xs py-1 px-2 rounded-lg border border-surface-200 bg-white text-surface-700 focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer disabled:opacity-50"
                          value={item.status || order.status || ''}
                          onChange={(e) => handleItemStatusChange(item.id, e.target.value)}
                          disabled={updatingItemId === item.id}
                          title="Change Item Status"
                        >
                          {STATUS_OPTS.map(s => (
                            <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <span className="font-mono font-semibold text-sm">₹{Number(item.total_price).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="space-y-1 text-sm">
            {[
              ['Subtotal', order.subtotal],
              ['Discount', `-${order.discount_amount}`],
              ['Shipping', order.shipping_amount],
              ['Tax',      order.tax_amount],
            ].map(([l,v]) => (
              <div key={l} className="flex justify-between text-surface-600">
                <span>{l}</span><span className="font-mono">₹{Number(v).toLocaleString()}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold text-surface-900 border-t border-surface-100 pt-1">
              <span>Total</span><span className="font-mono">₹{Number(order.total_amount).toLocaleString()}</span>
            </div>
          </div>

          {/* Status history */}
          {order.statusHistory?.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-surface-500 mb-2">Status History</p>
              <div className="space-y-1">
                {order.statusHistory.map(h => (
                  <div key={h.id} className="flex items-center gap-2 text-xs text-surface-500">
                    <span className="font-mono">{h.created_at ? format(new Date(h.created_at), 'dd MMM HH:mm') : ''}</span>
                    <span className="text-surface-300">→</span>
                    <StatusBadge status={h.to_status} />
                    {h.note && <span className="text-surface-400">— {h.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Update status — Only show if NOT isReadOnly */}
          {!isReadOnly ? (
            <div className="rounded-xl border border-brand-100 bg-brand-50 p-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-brand-700">Update Status</p>
              <div className="grid grid-cols-2 gap-3">
                <Select value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  <option value="">— Select new status —</option>
                  {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                </Select>
                <input className="input" value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" />
              </div>
              <Button variant="primary" size="sm" disabled={!newStatus} loading={updating} onClick={updateStatus}>
                Update Status
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
};

export default OrderDetailModal;
